import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getRouteLiveStock } from "../../services";
import { RouteLiveStockItem } from "../../types/dashboard.type";
import { numberWithCommas } from "../../utils";

interface RouteLiveStockModalProps {
  open: boolean;
  /** null เมื่อยังไม่ได้เลือกสาย — ใช้เป็นสัญญาณว่าไม่ต้องโหลด */
  routeUuid: string | null;
  routeCode: string;
  /** วันที่ธุรกิจในรูปแบบ YYYY-MM-DD */
  date: string;
  onClose: () => void;
}

const headerCellSx = { color: "#aeaeae", fontWeight: 600 };

/**
 * คำอธิบายว่าตัวเลขในตารางสัมพันธ์กันยังไง — ผูกไว้กับหัวคอลัมน์ "ของขึ้นรถ"
 * เพราะเป็นยอดตั้งต้นที่อีกสามคอลัมน์แบ่งกันไป
 */
const COLUMN_HELP = (
  <>
    ของขึ้นรถ = ขายแล้ว + คงเหลือ + ของเปลี่ยน
    <br />
    (ของขึ้นรถ = ของเบิก + ของยกมา)
    <br />
    <br />
    ของเสีย อาจไม่เท่ากับ ของเปลี่ยน เพราะมีการเปลี่ยนทดแทน
  </>
);

/**
 * สต็อกสดบนรถของสายส่งที่เลือก รายสินค้า
 *
 * โหลดตอนเปิดเท่านั้น (on-demand) ไม่ดึงมาพร้อมตารางหลัก เพราะเป็นข้อมูลรายสินค้า
 * ของสายเดียว ยิงทีละสายจึงถูกกว่าดึงล่วงหน้าทุกสาย
 *
 * คงเหลือมาจาก route_stock_item.qty ซึ่งเป็นยอดสดที่ตัดทุกการขายอยู่แล้ว และ
 * ของเสียเป็นยอดสะสมเรียลไทม์จากทุกร้านที่เยี่ยมแล้ว ไม่ใช่ยอดตอน reconcile
 *
 * "ขายแล้ว" นับเฉพาะการขายจริง ของที่จ่ายไปเปลี่ยนของเสียให้ร้านแยกอยู่คอลัมน์
 * "ของเปลี่ยน" — เดิมสองอย่างนี้ถูกรวมกัน ทำให้ยอดขายสูงเกินจริง 12-20% ทุกวัน
 *
 * ลำดับคอลัมน์เรียงตามสมการที่ tooltip อธิบาย: ของขึ้นรถ = ขายแล้ว + คงเหลือ +
 * ของเปลี่ยน แล้วปิดท้ายด้วยของเสีย ซึ่งอยู่นอกสมการ
 */
export default function RouteLiveStockModal({
  open,
  routeUuid,
  routeCode,
  date,
  onClose,
}: RouteLiveStockModalProps) {
  const [items, setItems] = useState<RouteLiveStockItem[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const loadStock = useCallback(async (uuid: string, businessDate: string) => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getRouteLiveStock(uuid, businessDate);
      setItems(data ?? []);
    } catch {
      // interceptor แสดง error ให้แล้ว — ตารางว่างสื่อว่าโหลดไม่สำเร็จพอ
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !routeUuid) return;
    setItems(null);
    loadStock(routeUuid, date);
  }, [open, routeUuid, date, loadStock]);

  const sumBy = (pick: (item: RouteLiveStockItem) => number) =>
    (items ?? []).reduce((sum, item) => sum + pick(item), 0);

  const totalWithdraw = sumBy((item) => item.product_withdraw);
  const totalUsed = sumBy((item) => item.used_qty);
  const totalRemaining = sumBy((item) => item.remaining_qty);
  const totalReplacement = sumBy((item) => item.replacement_qty);
  const totalWaste = sumBy((item) => item.waste_qty);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Box>
          <Typography variant="body1" fontWeight={600}>
            สต็อกบนรถ · สาย {routeCode}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ยอดปัจจุบัน ณ เวลาที่เปิดดู
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={40} thickness={5} />
          </Box>
        )}
        {!loading && items && items.length === 0 && (
          <Typography color="text.secondary" fontWeight={500} align="center" py={5}>
            ไม่พบข้อมูลสต็อกของสายนี้ในวันที่เลือก
          </Typography>
        )}
        {!loading && items && items.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f4f4f4" }}>
                <TableRow>
                  <TableCell sx={headerCellSx}>สินค้า</TableCell>
                  <TableCell align="center" sx={headerCellSx}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      ของขึ้นรถ
                      <Tooltip title={COLUMN_HELP} arrow enterTouchDelay={0}>
                        <InfoOutlinedIcon
                          sx={{ fontSize: 16, color: "#aeaeae", cursor: "help" }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={headerCellSx}>
                    ขายแล้ว
                  </TableCell>
                  <TableCell align="center" sx={headerCellSx}>
                    คงเหลือ
                  </TableCell>
                  <TableCell align="center" sx={headerCellSx}>
                    ของเปลี่ยน
                  </TableCell>
                  <TableCell align="center" sx={headerCellSx}>
                    ของเสีย
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.product_id}>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell align="center">
                      {numberWithCommas(item.product_withdraw)}
                    </TableCell>
                    <TableCell align="center">
                      {numberWithCommas(item.used_qty)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {numberWithCommas(item.remaining_qty)}
                    </TableCell>
                    <TableCell align="center">
                      {numberWithCommas(item.replacement_qty)}
                    </TableCell>
                    <TableCell align="center">
                      {numberWithCommas(item.waste_qty)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#fafafa" }}>
                  <TableCell sx={{ fontWeight: 600 }}>รวม</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {numberWithCommas(totalWithdraw)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {numberWithCommas(totalUsed)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {numberWithCommas(totalRemaining)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {numberWithCommas(totalReplacement)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {numberWithCommas(totalWaste)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}
